import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Monitor, Smartphone, Tablet, Globe, MapPin, Clock, Shield, ShieldOff,
  RefreshCw, Wifi, Chrome, Trash2, User, Fingerprint, Building2, Zap, ExternalLink
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = 'https://api.lifeshare.app/api/v1';

interface AdminSession {
  id: number;
  user_id: number;
  username: string;
  full_name: string | null;
  avatar_url: string | null;
  admin_role: string | null;
  browser: string;
  browser_version: string;
  os: string;
  device_type: string;
  ip_address: string;
  city: string;
  district: string;
  region: string;
  country: string;
  country_code: string;
  zip_code: string;
  latitude: string;
  longitude: string;
  timezone: string;
  isp: string;
  org: string;
  is_active: boolean;
  is_current: boolean;
  is_online: boolean;
  last_active: string | null;
  created_at: string | null;
  expires_at: string | null;
}

const getFlagEmoji = (code: string): string => {
  if (!code || code.length !== 2) return '';
  const upper = code.toUpperCase();
  const first = upper.charCodeAt(0) + 127397;
  const second = upper.charCodeAt(1) + 127397;
  return String.fromCodePoint(first, second);
};

const deviceIcon = (type: string) => {
  if (type === 'mobile') return <Smartphone className="w-5 h-5" />;
  if (type === 'tablet') return <Tablet className="w-5 h-5" />;
  return <Monitor className="w-5 h-5" />;
};

const browserIcon = (browser: string) => {
  const b = browser?.toLowerCase() || '';
  if (b.includes('chrome') || b.includes('edge')) return <Chrome className="w-4 h-4" />;
  if (b.includes('safari')) return <Globe className="w-4 h-4" />;
  return <Globe className="w-4 h-4" />;
};

const timeAgo = (dateStr: string | null): string => {
  if (!dateStr) return '-';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Şimdi';
  if (mins < 60) return `${mins}dk önce`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}sa önce`;
  const days = Math.floor(hours / 24);
  return `${days}g önce`;
};

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('tr-TR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

export default function AdminSessions() {
  const { token } = useAuth();
  const [sessions, setSessions] = useState<AdminSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'active' | 'expired'>('all');

  const fetchSessions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/panel-sessions`);
      setSessions(data.sessions || []);
    } catch (err) {
      console.error('Failed to load sessions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
    const interval = setInterval(fetchSessions, 30000);
    return () => clearInterval(interval);
  }, [fetchSessions]);

  // Heartbeat every 2 minutes
  useEffect(() => {
    const hb = setInterval(() => {
      axios.post(`${API}/admin/panel-sessions/heartbeat`).catch(() => {});
    }, 120000);
    return () => clearInterval(hb);
  }, []);

  // Record current session on mount
  useEffect(() => {
    if (token) {
      axios.post(`${API}/admin/panel-sessions/record`, {
        user_agent: navigator.userAgent
      }).catch(() => {});
    }
  }, [token]);

  const cleanupExpired = async () => {
    if (!window.confirm('Süresi dolmuş ve sonlandırılmış tüm oturumlar silinecek. Emin misiniz?')) return;
    try {
      const { data } = await axios.post(`${API}/admin/panel-sessions/cleanup`);
      alert(`${data.deleted} oturum temizlendi`);
      window.location.reload();
    } catch (err) {
      console.error('Cleanup failed:', err);
    }
  };

  const terminateSession = async (id: number) => {
    if (!window.confirm('Bu oturumu sonlandırmak istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/admin/panel-sessions/${id}`);
      fetchSessions();
    } catch (err) {
      console.error('Failed to terminate session:', err);
    }
  };

  const filtered = sessions.filter(s => {
    if (filter === 'active') return s.is_active;
    if (filter === 'expired') return !s.is_active;
    return true;
  });

  const activeSessions = sessions.filter(s => s.is_active);
  const onlineSessions = sessions.filter(s => s.is_online);

  return (
    <div className="space-y-6">
      <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">Admin Oturumları</h1>
          <p className="text-sm text-gray-500 mt-1">Admin hesaplarının giriş yaptığı cihaz, tarayıcı ve konum bilgileri</p>
        </div>
        <div className="flex items-center gap-2">
          {sessions.length > 1 && (
            <button onClick={cleanupExpired}
              className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-xl hover:bg-red-100 transition-colors text-sm font-medium">
              <Trash2 className="w-4 h-4" /> Eskilerini Temizle
            </button>
          )}
          <button onClick={() => { setLoading(true); fetchSessions(); }}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors text-sm font-medium">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Yenile
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
              <Monitor className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{sessions.length}</p>
              <p className="text-xs text-gray-500">Toplam Oturum</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{activeSessions.length}</p>
              <p className="text-xs text-gray-500">Aktif Oturum</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center">
              <Wifi className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{onlineSessions.length}</p>
              <p className="text-xs text-gray-500">Şu An Çevrimiçi</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2">
        {[
          { key: 'all', label: 'Tümü', count: sessions.length },
          { key: 'active', label: 'Aktif', count: activeSessions.length },
          { key: 'expired', label: 'Süresi Dolmuş', count: sessions.length - activeSessions.length },
        ].map(f => (
          <button key={f.key}
            onClick={() => setFilter(f.key as any)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              filter === f.key
                ? 'bg-purple-600 text-white'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}>
            {f.label} ({f.count})
          </button>
        ))}
      </div>

      {/* Sessions List */}
      {loading ? (
        <div className="flex justify-center py-16">
          <RefreshCw className="w-8 h-8 text-purple-400 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
          <ShieldOff className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">Oturum bulunamadı</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(s => (
            <div key={s.id} className={`bg-white rounded-2xl border p-5 transition-all ${
              s.is_current ? 'border-purple-200 ring-1 ring-purple-100' :
              s.is_active ? 'border-gray-100' : 'border-gray-100 opacity-60'
            }`}>
              <div className="flex items-start gap-4">
                {/* Device Icon */}
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  s.is_online ? 'bg-green-50 text-green-600' :
                  s.is_active ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-400'
                }`}>
                  {deviceIcon(s.device_type)}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900 text-sm">
                      {s.browser} {s.browser_version}
                    </span>
                    <span className="text-gray-400 text-xs">•</span>
                    <span className="text-gray-600 text-sm">{s.os}</span>

                    {s.is_current && (
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-[11px] font-semibold rounded-full">
                        Bu Cihaz
                      </span>
                    )}
                    {s.is_online && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-[11px] font-semibold rounded-full flex items-center gap-1">
                        <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                        Çevrimiçi
                      </span>
                    )}
                    {!s.is_active && (
                      <span className="px-2 py-0.5 bg-red-100 text-red-600 text-[11px] font-semibold rounded-full">
                        Sonlandırıldı
                      </span>
                    )}
                  </div>

                  {/* User + Time Row */}
                  <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
                    <div className="flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5 text-gray-400" />
                      <span className="font-medium text-gray-700">{s.full_name || s.username}</span>
                      {s.admin_role && (
                        <span className="text-[10px] text-purple-500">({s.admin_role === 'super_admin' ? 'SA' : 'Admin'})</span>
                      )}
                    </div>
                    <span className="text-gray-300">|</span>
                    <div className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5 text-gray-400" />
                      {timeAgo(s.last_active)}
                    </div>
                  </div>

                  {/* Location Card */}
                  <div className="mt-3 bg-gray-50 rounded-xl p-3 space-y-2">
                    {/* Full Address */}
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-gray-800">
                          {[s.district, s.city, s.region].filter(Boolean).join(', ') || '-'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {[s.country, s.zip_code ? `(${s.zip_code})` : ''].filter(Boolean).join(' ')}
                          {s.country_code ? ` ${getFlagEmoji(s.country_code)}` : ''}
                        </p>
                      </div>
                    </div>

                    {/* Details Row */}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-500">
                      <div className="flex items-center gap-1">
                        <Fingerprint className="w-3 h-3 text-gray-400" />
                        <span className="font-mono">{s.ip_address || '-'}</span>
                      </div>
                      {s.isp && (
                        <div className="flex items-center gap-1">
                          <Zap className="w-3 h-3 text-amber-400" />
                          <span>{s.isp}</span>
                        </div>
                      )}
                      {s.org && s.org !== s.isp && (
                        <div className="flex items-center gap-1">
                          <Building2 className="w-3 h-3 text-gray-400" />
                          <span>{s.org}</span>
                        </div>
                      )}
                      {s.timezone && (
                        <div className="flex items-center gap-1">
                          <Globe className="w-3 h-3 text-blue-400" />
                          <span>{s.timezone}</span>
                        </div>
                      )}
                      {s.latitude && s.longitude && s.latitude !== '' && s.longitude !== '' && (
                        <a
                          href={`https://maps.google.com/?q=${s.latitude},${s.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-blue-500 hover:text-blue-700 transition-colors"
                        >
                          <ExternalLink className="w-3 h-3" />
                          <span>Haritada Gör</span>
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Timestamps */}
                  <div className="mt-2 flex items-center gap-4 text-[11px] text-gray-400">
                    <span>Giriş: {formatDate(s.created_at)}</span>
                    <span>Bitiş: {formatDate(s.expires_at)}</span>
                  </div>
                </div>

                {/* Actions */}
                {s.is_active && !s.is_current && (
                  <button onClick={() => terminateSession(s.id)}
                    className="flex-shrink-0 p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                    title="Oturumu Sonlandır">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
    </div>
  );
}
